import { useEffect, useRef, useState, type MutableRefObject } from 'react';

/** Seuil RMS (0..1) au-delà duquel on considère que la personne parle — sert
 * d'indicateur visuel (icône micro qui pulse) ET de détecteur de « moteur de
 * reconnaissance sourd » (cf. `use-live-transcription.ts`). Même ordre de
 * grandeur que `SIGNAL_THRESHOLD` dans apps/app/.../mic-utils.ts. */
const VOLUME_THRESHOLD = 0.02;

export interface MicLevel {
  /** Vrai dès que le volume dépasse `VOLUME_THRESHOLD` — juste visuel (re-render à chaque bascule). */
  isSpeaking: boolean;
  /**
   * Pic RMS (0..1) depuis la dernière remise à zéro — SANS re-render. Le
   * LECTEUR le remet à 0 après lecture : un pic sur une fenêtre est plus fiable
   * qu'un échantillon instantané, qui tombe dans les creux entre deux syllabes.
   */
  peakRef: MutableRefObject<number>;
}

/** Gestes utilisateur qui autorisent un `AudioContext` à démarrer. */
const RESUME_EVENTS = ['pointerdown', 'pointerup', 'keydown', 'touchend'] as const;

/**
 * Indicateur "en train de parler" — AnalyserNode (Web Audio API) sur le micro
 * local déjà ouvert par WebRTC (pas de second `getUserMedia`, pas de second
 * prompt). PLATEFORME-AGNOSTIQUE : `AudioContext`/`AnalyserNode` fonctionnent
 * identiquement en WebView Capacitor (déjà prouvé par `getUserMedia`
 * caméra/micro natif dans ce même projet) — partagé entre les backends web ET
 * natif de la retranscription, qui eux divergent (Web Speech API vs plugin
 * Capacitor).
 *
 * ⚠️ Un `AudioContext` créé sans geste utilisateur démarre `suspended` (politique
 * d'autoplay Chrome/Safari) et son analyseur ne renvoie alors que du silence :
 * on le réveille dès le premier geste (le clic sur « Texte »/« Activer » en est
 * un), sinon `isSpeaking` resterait faux à jamais.
 */
export function useMicLevel(localStream: MediaStream | null): MicLevel {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const peakRef = useRef(0);

  useEffect(() => {
    const audioTracks = localStream?.getAudioTracks() ?? [];
    if (audioTracks.length === 0) return;

    const ctx = new AudioContext();
    const resume = () => {
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    };
    resume();
    for (const evt of RESUME_EVENTS) document.addEventListener(evt, resume);

    const source = ctx.createMediaStreamSource(new MediaStream(audioTracks));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;

    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sumSquares = 0;
      for (const v of data) {
        const normalized = (v - 128) / 128;
        sumSquares += normalized * normalized;
      }
      const rms = Math.sqrt(sumSquares / data.length);
      if (rms > peakRef.current) peakRef.current = rms;
      setIsSpeaking(rms > VOLUME_THRESHOLD);
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      for (const evt of RESUME_EVENTS) document.removeEventListener(evt, resume);
      void ctx.close();
    };
  }, [localStream]);

  return { isSpeaking, peakRef };
}
